import { Auth0Client } from "@auth0/auth0-spa-js";
import axios, { AxiosResponse } from "axios";

import { trackException } from "../Telemetry";
import { reqAuthInterceptor, reqNoopInterceptor } from "./request";

// Refresh the access token this long before it actually expires, so in-flight
// requests never carry a token that dies mid-flight.
const REFRESH_LEEWAY_MS = 2 * 60 * 1000;
const REFRESH_CHECK_INTERVAL_MS = 30 * 1000;

// A failed refresh waits this long before the next attempt, so an unreachable
// Auth0 doesn't mean a request every tick. Short enough to get two or three
// tries in within the leeway, rather than leaving the first blip to the 401
// interceptor.
const REFRESH_RETRY_DELAY_MS = 45 * 1000;

// A token that reads as near-expiry the moment it is minted means the schedule
// can't be trusted at all - back right off rather than retrying on every tick,
// for the life of the tab.
const REFRESH_SKEW_PAUSE_MS = 5 * 60 * 1000;

// Auth0 errors that mean the grant itself is gone - retrying can never succeed.
// missing_refresh_token is an auth0-spa-js v2 code; v1 falls back to the iframe
// and reports login_required instead.
const TERMINAL_AUTH_ERRORS = [
  "login_required",
  "consent_required",
  "interaction_required",
  "invalid_grant",
  "missing_refresh_token",
];

const isTerminalAuthError = (error: any): boolean => {
  return TERMINAL_AUTH_ERRORS.includes(error?.error);
};

let authClient: Auth0Client = null;
let accessToken: string = null;
let pendingRefresh: Promise<string> = null;
let sessionExpired = false;
let interceptorsRegistered = false;

// The provider owns the signed-out UI, so both refresh paths - the scheduled one
// and the 401 interceptor - report a dead session here.
const sessionExpiredSubscribers = new Set<(error: any) => void>();

/**
 * Set the access token used for API requests. A token arriving here also clears
 * any earlier session-expired state, so a fresh login revives refreshing
 * without needing a page load.
 */
export const setAccessToken = (token: string) => {
  accessToken = token;
  sessionExpired = false;
};

/**
 * Drop the access token used for API requests. Callers are responsible for
 * reflecting the signed-out state in the UI.
 */
const clearAccessToken = () => {
  accessToken = null;
};

/**
 * The current access token, or null. Only for callers that have to attach it by
 * hand - mapbox builds its tile requests outside axios. Anything going through
 * axios is served by reqAuthInterceptor instead.
 */
export const getAccessToken = (): string => {
  return accessToken;
};

/**
 * Milliseconds since the epoch at which a JWT expires, or 0 if it can't be read.
 */
const tokenExpiresAt = (token: string): number => {
  try {
    // JWT segments are base64url encoded, atob only accepts standard base64
    const payload = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const { exp } = JSON.parse(atob(payload));

    return typeof exp === "number" ? exp * 1000 : 0;
  } catch (err) {
    // without an expiry there is nothing to schedule, so say so rather than
    // silently falling back to the 401 interceptor
    console.warn("Could not read the access token expiry", err);

    return 0;
  }
};

/**
 * Ask Auth0 for the token.
 *
 * A cached one will do when it outlives the leeway: the cache is shared across
 * tabs, so it may hold a token another tab fetched seconds ago, and every
 * ignoreCache call is a round trip that rotates the refresh token. Auth0 only
 * counts a cached entry stale within a minute of expiry - well inside our
 * leeway - so a near-expiry hit still has to be forced.
 */
const fetchToken = async (allowCached?: boolean): Promise<string> => {
  if (allowCached) {
    const cached = await authClient.getTokenSilently();

    if (tokenExpiresAt(cached) - Date.now() > REFRESH_LEEWAY_MS) {
      return cached;
    }
  }

  return authClient.getTokenSilently({ ignoreCache: true });
};

/**
 * Ask Auth0 for a new token, sharing the request with anyone who asks while it
 * is still in flight.
 */
const requestToken = (allowCached?: boolean): Promise<string> => {
  const refresh = fetchToken(allowCached).then(
    (token) => {
      setAccessToken(token);

      return token;
    },
    (error) => {
      if (isTerminalAuthError(error)) {
        sessionExpired = true;
        clearAccessToken();
        sessionExpiredSubscribers.forEach((notify) => notify(error));
      }

      throw error;
    }
  );

  pendingRefresh = refresh;
  const settled = () => {
    if (pendingRefresh === refresh) {
      pendingRefresh = null;
    }
  };
  refresh.then(settled, settled);

  return refresh;
};

/**
 * Refresh the access token. Auth0 rotates refresh tokens, so two refreshes at
 * once can trip reuse detection and invalidate the whole grant - callers share
 * a single request instead.
 *
 * Pass force when the caller needs claims that changed server-side just now -
 * an already in-flight refresh may have been issued before that change
 * committed, so its token can't be reused.
 *
 * Pass allowCached when any token that outlives the leeway will do. Only the
 * scheduled refresh can say that: the 401 path has just had a token rejected,
 * and force exists precisely because the cache is behind.
 */
export const refreshAccessToken = (
  options: { force?: boolean; allowCached?: boolean } = {}
): Promise<string> => {
  if (sessionExpired) {
    // the grant is gone - fail without another doomed round trip
    return Promise.reject(new Error("The session has expired"));
  }

  if (!pendingRefresh) {
    return requestToken(options.allowCached);
  }

  if (options.force) {
    // queue behind the in-flight refresh rather than racing it
    return pendingRefresh
      .catch(() => undefined)
      .then(() => refreshAccessToken());
  }

  return pendingRefresh;
};

/**
 * Keep the access token fresh for as long as the page stays open. The token is
 * only fetched once at login, so without this every request made after it
 * expires fails until the user reloads the page.
 * Returns a cleanup function.
 */
export const startTokenRefresh = (config: {
  onSessionExpired(error: any): void;
}): (() => void) => {
  let retryAfter = 0;
  let unschedulable: string = null;

  const maybeRefresh = async () => {
    // no token is nothing to schedule, and one we couldn't read won't become
    // readable - either way, wait for it to change rather than re-reading it
    if (Date.now() < retryAfter || accessToken === unschedulable) {
      return;
    }

    const expiresAt = accessToken ? tokenExpiresAt(accessToken) : 0;

    if (!expiresAt) {
      // no schedule to work from - leave it to the 401 interceptor
      unschedulable = accessToken;

      return;
    }

    if (Date.now() < expiresAt - REFRESH_LEEWAY_MS) {
      return;
    }

    try {
      const token = await refreshAccessToken({ allowCached: true });

      // a brand new token that still reads as near-expiry means a clock far
      // enough ahead that the schedule is worth nothing
      if (Date.now() >= tokenExpiresAt(token) - REFRESH_LEEWAY_MS) {
        retryAfter = Date.now() + REFRESH_SKEW_PAUSE_MS;
      }
    } catch (err) {
      // terminal failures arrive via onSessionExpired; anything else is assumed
      // transient and retried, after a pause
      retryAfter = Date.now() + REFRESH_RETRY_DELAY_MS;

      // nothing rejects on this path, so a tab that silently stops refreshing
      // for its whole lifetime would otherwise never show up
      trackException(
        err,
        { source: "tokenRefresh", trigger: "scheduled" },
        { dedupeKey: "tokenRefresh:scheduled" }
      );
      console.error(err);
    }
  };

  const onVisibilityChange = () => {
    if (document.visibilityState === "visible") {
      maybeRefresh();
    }
  };

  const onSessionExpired = (error: any) => {
    // the grant is gone - no amount of retrying brings it back
    stop();
    config.onSessionExpired(error);
  };

  const stop = () => {
    sessionExpiredSubscribers.delete(onSessionExpired);
    clearInterval(interval);
    document.removeEventListener("visibilitychange", onVisibilityChange);
  };

  sessionExpiredSubscribers.add(onSessionExpired);

  // timers are throttled (or frozen, if the machine sleeps) in background tabs,
  // so re-check as soon as the tab becomes visible again
  document.addEventListener("visibilitychange", onVisibilityChange);
  const interval = setInterval(maybeRefresh, REFRESH_CHECK_INTERVAL_MS);

  // the page may well have loaded with a token that's already near expiry
  maybeRefresh();

  return stop;
};

/**
 * Noop response interceptor.
 */
export const resSuccessInterceptor = (config?: any) => {
  return async (response: AxiosResponse): Promise<any> => {
    return response;
  };
};

/**
 * Unauthorized response interceptor.
 * The response interceptor checks to see if the API returned a 401 status code
 * due to an expired token. It then refreshes the token and replays the request.
 */
export const resErrorInterceptor = () => {
  return async (error: any): Promise<any> => {
    const request = error?.config;

    // only a request that actually carried the token can be failing because
    // that token expired. An anonymous 401 is the API's answer, and a 401 from
    // a host we never send the token to is none of our business
    if (
      error?.response?.status === 401 &&
      request?.headers?.Authorization &&
      !request._retry
    ) {
      request._retry = true;

      // another path may have renewed the token while this request was in
      // flight - replaying with that beats paying for a second refresh
      const current = getAccessToken();
      const renewed =
        current && request.headers.Authorization !== `Bearer ${current}`;

      let token: string;
      try {
        token = renewed ? current : await refreshAccessToken();
      } catch (err) {
        // no point replaying the request with the token that just got rejected
        // report the refresh failure itself - the caller only ever sees the
        // original 401, so this is the last place the real cause exists
        trackException(
          err,
          { source: "tokenRefresh", trigger: "replay401" },
          { dedupeKey: "tokenRefresh:replay401" }
        );
        console.error(err);

        return Promise.reject(error);
      }

      // this request already carried a token, so replace it here rather than
      // relying on reqAuthInterceptor to recognise the url on the replay
      request.headers.Authorization = `Bearer ${token}`;

      return axios.request(request);
    }

    return Promise.reject(error);
  };
};

/**
 * Point the auth interceptors at the current Auth0 client, attaching them to
 * axios on the first call. Registering again would stack duplicates, so later
 * calls only swap the client.
 *
 * apiUrl is the only origin the token is sent to - see reqAuthInterceptor.
 */
export const registerAuthInterceptors = (
  client: Auth0Client,
  apiUrl: string
) => {
  authClient = client;

  if (interceptorsRegistered) {
    return;
  }
  interceptorsRegistered = true;

  if (!apiUrl) {
    // the allow-list is what makes it safe to hold a token at all, so a missing
    // one means no request is authorized - say so rather than 401 everywhere
    trackException(
      new Error("No API url configured - API requests will not be authorized"),
      { source: "authInterceptors" },
      { dedupeKey: "authInterceptors:noApiUrl" }
    );
    console.error(
      "No API url configured - API requests will not be authorized"
    );
  }

  axios.interceptors.request.use(reqNoopInterceptor());
  axios.interceptors.request.use(
    reqAuthInterceptor({ apiUrl, getAccessToken })
  );
  axios.interceptors.response.use(
    resSuccessInterceptor(),
    resErrorInterceptor()
  );
};
